import React, { useCallback, useMemo, useRef } from "react";

import { useContentLocale } from "@/i18n/hooks";
import { Turnstile } from "@marsidev/react-turnstile";
import type { TurnstileInstance } from "@marsidev/react-turnstile";

/**
 * How long to wait for an invisible Turnstile challenge before giving up.
 *
 * `getResponsePromise()` has no timeout of its own: when the widget never
 * solves (a site key whose allowed hostnames exclude this origin, a Cloudflare
 * outage, a blocked challenges.cloudflare.com) it simply never settles. Any
 * caller that awaits it then hangs forever, which on a form means a spinner
 * that never stops and no error the user can act on. Resolving without a token
 * is the safe end of that race: the request still goes out, the server decides,
 * and the user sees a real answer instead of a frozen button.
 */
const TOKEN_TIMEOUT_MS = 8000;

export const useTurnstile = () => {
  const ref = useRef<TurnstileInstance | null>(null);
  // Turnstile takes a fixed language list; `/en-us` reads `en` (WHO-5766).
  const language = useContentLocale();

  const getToken = useCallback(async () => {
    if (!ref.current) {
      return;
    }

    const widget = ref.current;

    /** Await for the result here, so that the caller does not need to remember to use try...catch */
    try {
      let timer: ReturnType<typeof setTimeout> | undefined;
      const token = await Promise.race([
        widget.getResponsePromise(),
        new Promise<undefined>(resolve => {
          timer = setTimeout(() => resolve(undefined), TOKEN_TIMEOUT_MS);
        }),
      ]);
      if (timer) {
        clearTimeout(timer);
      }
      // Reset either way, so a second attempt starts from a clean widget
      // rather than inheriting the challenge that just failed to solve.
      widget.reset();
      return token;
    } catch (ex) {
      console.error(ex);
    }
  }, []);

  const getHeaders = useCallback(async () => {
    const isPreviewOrDev =
      process.env.NEXT_PUBLIC_VERCEL_ENV === "preview" ||
      process.env.NODE_ENV === "development";

    const skiptoken = isPreviewOrDev
      ? process.env.NEXT_PUBLIC_TURNSTILE_SKIP
      : undefined;

    // With a skip token configured (preview and dev only, never production)
    // the challenge is not the gate, so waiting on the widget only adds
    // latency to every submit.
    const token = skiptoken ? undefined : await getToken();

    const headers: Record<string, string> = {};

    if (token) {
      headers["X-Turnstile-Token"] = token;
    }

    if (skiptoken) {
      headers["X-Turnstile-Skip-Token"] = skiptoken;
    }

    return headers;
  }, [getToken]);

  const Component = useMemo(() => {
    return (props: { action: string }) => {
      if (!process.env.NEXT_PUBLIC_TURNSTILE_SITE_KEY) {
        return null;
      }

      return (
        <Turnstile
          ref={ref}
          siteKey={process.env.NEXT_PUBLIC_TURNSTILE_SITE_KEY}
          options={{
            ...props,
            size: "invisible",
            theme: "light",
            tabIndex: -1,
            language,
          }}
        />
      );
    };
  }, [language]);

  return { Component, getToken, getHeaders };
};
